/**
 * Columns provider to the inventory table.
 * This displays a row representing a tag with its current value, new target value, and linked asset.
 */
import React from 'react';
import { observer } from 'mobx-react';
import { AssetTagsStore } from 'app/stores/assetTags/$assetTags';
import pluralize from '@kentik/ui-shared/util/pluralize';
import { AssetType, getLabelTypeValue } from '@kentik/ui-shared/labels/lib';
import AssetModelRenderer, { AssetModel } from 'app/views/settings/assetTags/renderers/AssetModelRenderer';
import currentValueRenderer from 'app/views/settings/assetTags/renderers/currentValueRenderer';
import targetValueRenderer from 'app/views/settings/assetTags/renderers/targetValueRenderer';

interface Props {
  children: (columns: unknown[]) => JSX.Element;
  $assetTags: AssetTagsStore;
  assetType: AssetType;
  singleMode?: boolean;
}

function InventoryColumns(props: Props): JSX.Element {
  const { children, $assetTags, assetType, singleMode } = props;
  const assetTypeLabel = pluralize(getLabelTypeValue(assetType), 0);
  const tagNameColumn = { name: 'tag_name', label: 'Attribute Key' };
  const tagDescriptionColumn = { name: 'description', label: 'Description' };
  const currentValueColumn = {
    name: 'valueEdited',
    label: 'Current Value',
    computed: true,
    width: 175,
    sortable: false,
    renderer: currentValueRenderer
  };
  const currentAssetsColumn = { name: 'current_assets', label: `Current ${assetTypeLabel}`, sortable: false };
  const targetValueColumn = {
    name: 'target_value',
    label: 'Target Value',
    flexBasis: 150,
    sortable: false,
    renderer: targetValueRenderer
  };
  const targetAssetColumn = {
    name: 'asset_name',
    label: `Target ${assetTypeLabel}`,
    flexBasis: 150,
    sortable: false,
    renderer: ({ model, value }: { model: AssetModel; value: string | number }) => (
      <AssetModelRenderer assetType={assetType} model={model} value={value} />
    )
  };
  const actionsColumn = { name: 'actions', label: '', width: 60 };

  // assemble column definitions depending on mode

  if (singleMode) {
    const items = [{ ...tagNameColumn, width: 200 }, { ...targetValueColumn, width: 350 }, tagDescriptionColumn];

    if ($assetTags.canUpdate) {
      items.push(actionsColumn);
    }

    return children(items);
  }

  return children([
    tagNameColumn,
    currentValueColumn,
    currentAssetsColumn,
    targetValueColumn,
    targetAssetColumn,
    actionsColumn
  ]);
}

export default observer(InventoryColumns);
